import { match } from 'ts-pattern';
import { memoEval } from '@salsita-ai/platform-project-sdk/domain';
import { $TopWithLengthAndWidth } from '../__generated/enhanced-model.js';
import { implementCustomExpressions, implementCustomInvariants } from '../__generated/implementation.js';
import { ColorOak, EdgeFinish, FenixVariant, TopColorCeramic, TopColorFenix, TopFinish, TopMaterial, TopWithLengthAndWidth } from '../__generated/model/index.js';
import { TOP_SHAPE_LABEL_MAP } from '../data/labels.js';
import { TopProperties } from '../data/materials.js';
import { EDGE_FINISH_OPTIONS, EDGE_FINISH_OPTION_MAP, FENIX_VARIANT_OPTIONS, FENIX_VARIANT_OPTION_MAP, TOP_FINISH_OPTIONS, TOP_FINISH_OPTION_MAP, TOP_MATERIAL_OPTIONS, TOP_MATERIAL_OPTION_MAP } from '../data/options.js';
import { CERAMIC_PRICE_MAP, COLOR_OAK_PRICE_MAP, EDGE_FINISH_PRICE_MAP, TOP_COLOR_FENIX_PRICE_MAP, TOP_FINISH_PRICE_MAP_LARGE, TOP_FINISH_PRICE_MAP_SMALL } from '../data/prices.js';
import { ColorOakSwatchMap, TopColorCeramicSwatchMap, TopColorFenixSwatchMap } from '../data/swatches.js';
export const topInvariants = implementCustomInvariants({
    Top: {
        fenixTopWithoutVariant: (self)=>{
            return self.material !== TopMaterial.Fenix || self.fenixVariant !== null;
        },
        oakTopWithoutFinish: (self)=>{
            return self.material !== TopMaterial.SolidOak || self.topFinish !== null;
        },
        roundEdgeFinishNotSupported: (self)=>{
            return self.edgeFinish !== EdgeFinish.Round || self.material === TopMaterial.SolidOak;
        },
        thicknessNotAvailableForThisMaterial: (self)=>{
            return self.$availableThicknesses.includes(self.thickness);
        },
        topColorNotAvailableForThisMaterial: (self)=>{
            return self.$availableColors.includes(self.color);
        },
        topFinishNotAvailableForThisMaterial: (self)=>{
            return self.$availableTopFinishes.includes(self.topFinish);
        },
        diameterNotAvailableForThisMaterial: (self)=>{
            return $TopWithLengthAndWidth.is(self) || self.$availableDiameters.includes(self.diameter);
        }
    }
});
const AVAILABLE_COLORS_SOLID_OAK = [
    ColorOak.UltraMatteLak,
    ColorOak.Oyster,
    ColorOak.DarkOak,
    ColorOak.Charcoal,
    ColorOak.SlateGrey,
    ColorOak.CottonWhite,
    ColorOak.Black
];
const AVAILABLE_COLORS_CERAMIC = [
    TopColorCeramic.CuarzoReno,
    TopColorCeramic.NeroZimbabwe,
    TopColorCeramic.BiancoSupreme,
    TopColorCeramic.CalacataVaglie,
    TopColorCeramic.TajMahal,
    TopColorCeramic.MacchiaVecchia,
    TopColorCeramic.CalacataNaturel,
    TopColorCeramic.BlueStoneNegro,
    TopColorCeramic.CeppoDeGre,
    TopColorCeramic.NeroArdi,
    TopColorCeramic.PaladioNaturel,
    TopColorCeramic.CalceGrigio,
    TopColorCeramic.TitanCemento,
    TopColorCeramic.LavaCorten,
    TopColorCeramic.BasicNieve,
    TopColorCeramic.Travertin,
    TopColorCeramic.CalacataGold,
    TopColorCeramic.SurfaceSand,
    TopColorCeramic.SurfaceOffWhite,
    TopColorCeramic.NoirDesir,
    TopColorCeramic.SurfaceCoolGrey,
    TopColorCeramic.HyperWhite,
    TopColorCeramic.DarkBlack,
    TopColorCeramic.ClassicCarrara
];
const AVAILABLE_COLORS_FENIX = [
    TopColorFenix.NeroIngo0720,
    TopColorFenix.BiancoKos0032,
    TopColorFenix.BeigeLuxor0719,
    TopColorFenix.VerdeComodoro0750,
    TopColorFenix.VerdeBrac0773
];
const AVAILABLE_THICKNESSES_SOLID_OAK = [
    30,
    40
];
const AVAILABLE_THICKNESSES_CERAMIC = [
    12
];
const AVAILABLE_THICKNESSES_FENIX_BIRCH = [
    30
];
const AVAILABLE_THICKNESSES_FENIX_SOLID = [
    12
];
const ALL_THICKNESSES = [
    12,
    30,
    40
];
const THICKNESS_OPTIONS = ALL_THICKNESSES.map((thickness)=>({
        value: thickness,
        label: `${thickness} mm`
    }));
const AVAILABLE_EDGE_FINISHES_SOLID_OAK = [
    EdgeFinish.Standard,
    EdgeFinish.Facet,
    EdgeFinish.Round
];
const AVAILABLE_EDGE_FINISHES_CERAMIC_FENIX = [
    EdgeFinish.Standard,
    EdgeFinish.Facet
];
const AVAILABLE_FENIX_VARIANTS_SOLID_OAK_CERAMIC = [
    FenixVariant.None
];
const AVAILABLE_FENIX_VARIANTS_FENIX = [
    FenixVariant.Solid,
    FenixVariant.Birch
];
const AVAILABLE_TOP_FINISHES_SOLID_OAK = [
    TopFinish.NewOak,
    TopFinish.LivedOak,
    TopFinish.RefinedOak
];
const AVAILABLE_TOP_FINISHES_CERAMIC_FENIX = [
    TopFinish.None
];
const getCompoundKey = ($top)=>{
    const { color, material, topFinish } = $top;
    return match(material).with(TopMaterial.SolidOak, ()=>`${material}_${topFinish}_${color}`).with(TopMaterial.Ceramic, TopMaterial.Fenix, ()=>`${material}_${color}`).exhaustive();
};
export const topExpressions = implementCustomExpressions({
    Top: {
        $availableColors: memoEval((self)=>[
                self.material
            ], (material)=>match(material).with(TopMaterial.SolidOak, ()=>AVAILABLE_COLORS_SOLID_OAK).with(TopMaterial.Ceramic, ()=>AVAILABLE_COLORS_CERAMIC).with(TopMaterial.Fenix, ()=>AVAILABLE_COLORS_FENIX).exhaustive()),
        $availableEdgeFinishes: memoEval((self)=>[
                self.material
            ], (material)=>match(material).with(TopMaterial.SolidOak, ()=>AVAILABLE_EDGE_FINISHES_SOLID_OAK).with(TopMaterial.Ceramic, TopMaterial.Fenix, ()=>AVAILABLE_EDGE_FINISHES_CERAMIC_FENIX).exhaustive()),
        $availableFenixVariants: memoEval((self)=>[
                self.material
            ], (material)=>match(material).with(TopMaterial.Fenix, ()=>AVAILABLE_FENIX_VARIANTS_FENIX).with(TopMaterial.SolidOak, TopMaterial.Ceramic, ()=>AVAILABLE_FENIX_VARIANTS_SOLID_OAK_CERAMIC).exhaustive()),
        $availableThicknesses: memoEval((self)=>[
                self.fenixVariant,
                self.material
            ], (fenixVariant, material)=>match(material).with(TopMaterial.SolidOak, ()=>AVAILABLE_THICKNESSES_SOLID_OAK).with(TopMaterial.Ceramic, ()=>AVAILABLE_THICKNESSES_CERAMIC).with(TopMaterial.Fenix, ()=>fenixVariant === FenixVariant.Birch ? AVAILABLE_THICKNESSES_FENIX_BIRCH : AVAILABLE_THICKNESSES_FENIX_SOLID).exhaustive()),
        $availableTopFinishes: memoEval((self)=>[
                self.material
            ], (material)=>match(material).with(TopMaterial.SolidOak, ()=>AVAILABLE_TOP_FINISHES_SOLID_OAK).with(TopMaterial.Ceramic, TopMaterial.Fenix, ()=>AVAILABLE_TOP_FINISHES_CERAMIC_FENIX).exhaustive()),
        $descriptionEdgeFinish: (self)=>EDGE_FINISH_OPTION_MAP[self.edgeFinish].label,
        $descriptionFenixVariant: (self)=>FENIX_VARIANT_OPTION_MAP[self.fenixVariant].label,
        $descriptionMaterial: (self)=>TOP_MATERIAL_OPTION_MAP[self.material].label,
        $descriptionShape: (self)=>TOP_SHAPE_LABEL_MAP[self.type],
        $descriptionTopColor: (self)=>{
            if (ColorOak.is(self.color)) {
                return ColorOakSwatchMap[self.color].swatchName;
            }
            if (TopColorCeramic.is(self.color)) {
                return TopColorCeramicSwatchMap[self.color].swatchName;
            }
            return TopColorFenixSwatchMap[self.color].swatchName;
        },
        $descriptionTopFinish: (self)=>TOP_FINISH_OPTION_MAP[self.topFinish].label,
        $edgeFinishFacet: (self)=>self.edgeFinish === EdgeFinish.Facet ? 1 : 0,
        $edgeFinishRound: (self)=>self.edgeFinish === EdgeFinish.Round ? 1 : 0,
        $price: memoEval((self)=>[
                self.$basePrice,
                self.color,
                self.edgeFinish,
                self.topFinish,
                TopWithLengthAndWidth.is(self) && self.length > 2600
            ], ($basePrice, color, edgeFinish, topFinish, hasLargeTop)=>{
            const ceramicPrice = TopColorCeramic.is(color) ? CERAMIC_PRICE_MAP[color] : 0;
            const fexixColorPrice = TopColorFenix.is(color) ? TOP_COLOR_FENIX_PRICE_MAP[color] : 0;
            const oakColorPrice = ColorOak.is(color) ? COLOR_OAK_PRICE_MAP[color] : 0;
            const topFinishPriceMap = hasLargeTop ? TOP_FINISH_PRICE_MAP_LARGE : TOP_FINISH_PRICE_MAP_SMALL;
            const topFinishPrice = topFinishPriceMap[topFinish];
            const edgeFinishPrice = EDGE_FINISH_PRICE_MAP[edgeFinish];
            return $basePrice + ceramicPrice + fexixColorPrice + oakColorPrice + topFinishPrice + edgeFinishPrice;
        }),
        $offsetY: (self)=>self.$table.height / 2,
        $edgeFinishOptions: ()=>EDGE_FINISH_OPTIONS,
        $fenixVariantOptions: ()=>FENIX_VARIANT_OPTIONS,
        $thicknessOptions: ()=>THICKNESS_OPTIONS,
        $topFinishOptions: ()=>TOP_FINISH_OPTIONS,
        $topMaterialOptions: ()=>TOP_MATERIAL_OPTIONS,
        $materialToOverride: (s)=>TopProperties[getCompoundKey(s)]?.materialsToOverride ?? [],
        $materialColor: (s)=>TopProperties[getCompoundKey(s)]?.color,
        $textureUrl: (s)=>TopProperties[getCompoundKey(s)]?.textureUrl,
        $normalMapUrl: (s)=>TopProperties[getCompoundKey(s)]?.normalMapUrl,
        $roughnessMapUrl: (s)=>TopProperties[getCompoundKey(s)]?.roughnessMapUrl,
        $roughness: (s)=>TopProperties[getCompoundKey(s)]?.roughness,
        $normalScale: (s)=>TopProperties[getCompoundKey(s)]?.normalScale,
        $envMapIntensity: (s)=>TopProperties[getCompoundKey(s)]?.envMapIntensity
    }
});

//# sourceMappingURL=top.js.map